import { Component, OnInit, ViewChild, ViewEncapsulation, signal, Inject } from '@angular/core';
import {
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';

import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { OtpDialogComponent } from '../otp-dialog/otp-dialog.component';
import { CommonModule, DOCUMENT } from '@angular/common';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { ConsentDialogComponent } from '../consent-dialog/consent-dialog.component';
import { IframeSecurityService } from 'app/services/iframeSecurity.service';
import { FerrerAuthService } from 'app/services/authentication.service';
import { DialogUtilsService } from 'app/modules/utils/dialog-utility.service';
import { OtpChangePasswordDialogComponent } from '../otp-changePassword/otpchangepwd-dialog.component';
import { OtpSuggestDialogComponent } from '../otp-suggest/otpsuggestpwd-dialog.component';
import { BlockedUserComponent } from 'app/modules/register-steps/blocked-user/blocked_user.component';
import { BlockedMedicalComponent } from 'app/modules/register-steps/non-medical/non_medical.component';
import { DiffCountryDialogComponent } from '../diffcountry-dialog/diffcountry-dialog.component';
import { SessionService } from 'app/services/session.service';
import { EmailVerifyStepComponent } from 'app/modules/register-steps/email-verify-window/email_verify_window.component';
import { NavigationFlowService } from 'app/services/navigation-flow.service';
@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CommonModule,
    TranslocoPipe,
  ],
})
export class LoginDialogComponent implements OnInit {
  @ViewChild('unlockSessionNgForm') unlockSessionNgForm: NgForm;

  loginSessionForm: FormGroup;

  showAlert: boolean = false;
  unlockSessionForm: UntypedFormGroup;
  private resizeListener: (() => void) | null = null;
  emailApiError: string | null = null;
  apiResendEmailError: string | null = null;
  passwordApiError: string | null = null;
  isLoading: boolean = false;
  public titleParams = { suffix: '' }; // Initialize empty

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    @Inject(MAT_DIALOG_DATA) public data: { language: string },
    private _authService: AuthService,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private _userService: UserService,
    private _dialogRef: MatDialogRef<any>,
    private _dialog: MatDialog,
    private translocoService: TranslocoService,
    @Inject(DOCUMENT) private document: Document,
    private iframeSecurityService: IframeSecurityService,
    private dialogUtilsService: DialogUtilsService,
    private ferrerAuth: FerrerAuthService,
    private sessionService: SessionService,
    private navigationFlow: NavigationFlowService
  ) { }

  /**
   * On init
   */
  ngOnInit(): void {
    // Create the form
    this.loginSessionForm = this._formBuilder.group({
      email: ['',
        [
          Validators.required,
          LoginDialogComponent.noWhitespaceValidator,
          Validators.email
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(16), LoginDialogComponent.noWhitespaceValidator
      ]],
    });

    if (window.parent !== window) {
      // We're in an iframe
      this.setupIframeMode();
    }
    if (this.data?.language) {

    }
    this.titleParams = { suffix: '' };

    this.sessionService.digitalAsset$.subscribe(digitalAsset => {
      if (digitalAsset) {
        console.log('Digital asset updated:', digitalAsset);
        this.titleParams = { suffix: digitalAsset };
      }
    });
  }

  private setupIframeMode(): void {
    try {
      // Check origin security first
      const isAllowedOrigin = this.iframeSecurityService.checkOriginSecurity();

      if (!isAllowedOrigin) {
        console.warn('Unauthorized embedding attempt detected');
        this.iframeSecurityService.handleUnauthorizedEmbedding();
        return;
      }

      // Add a class to the body to enable special iframe styling
      this.document.body.classList.add('in-iframe-mode');

      // Hide any navigation or other elements that shouldn't be visible in iframe mode
      const appHeader = this.document.querySelector('app-header') as HTMLElement;
      const appSidebar = this.document.querySelector('app-sidebar') as HTMLElement;

      if (appHeader) {
        appHeader.style.display = 'none';
      }

      if (appSidebar) {
        appSidebar.style.display = 'none';
      }

      // Calculate dialog size for the parent
      const dialogSize = this.dialogUtilsService.calculateDialogSize();

      // Get the parent origin that was stored during security check
      const parentOrigin = this.iframeSecurityService.getParentOrigin();

      // Notify parent that we're in iframe mode and send our preferred size
      if (window.parent) {
        try {
          window.parent.postMessage({
            type: 'iframeReady',
            dialogSize: dialogSize,
            // Include parent origin so parent knows we've verified it
            verifiedOrigin: parentOrigin
          }, parentOrigin || '*'); // Target specific origin if we know it, for security
        } catch (error) {
          console.error('Error sending message to parent:', error);
        }
      }
    } catch (error) {
      console.error('Error setting up iframe mode:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  changeLang(lang: string) {
    this.translocoService.setActiveLang(lang);
  }
  getEmailErrorMessage() {
    const emailControl = this.loginSessionForm.get('email');

    if (emailControl?.hasError('required')) return 'Introduce tu correo electrónico';
    if (emailControl?.hasError('email')) return 'Escribe el correo con el formato correo@dominio.com';
    if (emailControl?.hasError('containsSpaces')) return 'El correo no debe contener espacios en blanco';

    return '';
  }

  getPasswordErrorMessage() {
    const passControl = this.loginSessionForm.get('password');
    if (passControl?.hasError('required')) return this.translocoService.translate("login.passwordMissing");
    if (passControl?.hasError('minlength')) {
      return this.translocoService.translate("register.emailVerified_PasswordTip");
    }
    if (passControl?.hasError('maxlength')) {
      return this.translocoService.translate("register.emailVerified_PasswordTip");
    }
    if (passControl?.hasError('containsSpaces')) return this.translocoService.translate("login.blankSpaces");

    return '';
  }


  login(): void {
    if (!this.loginSessionForm) {
      console.error('Form is not initialized');
      return;
    }

    this.emailApiError = null;
    this.apiResendEmailError = null;
    this.passwordApiError = null;

    const parentOrigin = this.iframeSecurityService.getParentOrigin();
    // Return if the form is invalid
    if (this.loginSessionForm.invalid) {
      this.loginSessionForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    // Hide the alert
    this.showAlert = false;
    if (this.loginSessionForm.invalid) {
      this.showAlert = true;

      return;
    }
    const credentials = {
      email: this.loginSessionForm.get('email').value,
      password: this.loginSessionForm.get('password').value
    };
    this.ferrerAuth.login(credentials.email, credentials.password)
      .subscribe({
        next: (response) => {

          this.isLoading = false;
          if (response && response.userAccess?.termsAccepted === false) {
            const currentCountry = JSON.parse(localStorage.getItem('currentCountryInfo'));
            if (response.userAccess?.country !== currentCountry.country) {
              //open the one that detects different country
              this.opendiffCountryDialog(response);
            } else {
              this.openConsentDialog(response);
            }
            return;
          }
          if (response && response.userAccess?.success === true) {
            // variara segun el contenido
            const isInIframe = this.isRunningInIframe();
            if (isInIframe) {
              // We're in an iframe, post message to parent
              if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                  type: 'loginSuccess',
                  userData: {
                    email: this.loginSessionForm.get('email').value,
                    redirectUrl: response.returnUrl,
                    response: response  // TODO: este campo no debería exponerse
                  }
                }, '*');
              }
            } else {
              // Not in an iframe, open in new tab
              this._dialogRef.close("response");

              // Navigate to the returnUrl in the same tab
              window.location.href = response.returnUrl;

            }

            // Close the dialog
            this._dialogRef.close("response");
          } else {
            this.isLoading = false;

            this.handleApiError(response);
          }
        },
        error: (err) => {
          console.error('Login failed', err)
          this.isLoading = false;
          this.handleApiError(err);
        }
      });


  }

  handleApiError(error: any): void {
    console.error('Login error:', error);

    // Default error message
    let errorMessage = 'An unexpected error occurred';

    // Get error response body
    const errorBody = error.error.message || error.error || error;
    const errorCode = error.code || error.error.code || '';
    if (errorBody.includes('user has not accepted all consents') ||
      errorBody.includes('consents not accepted')) {

      //this.openConsentDialog();
      return;
    }

    if (errorBody.includes('Prohibido el acceso')) {
      this.openBlockedDialog();
      return;
    }

    if (errorBody.includes('LOGIN_REQUEST_WAS_NOT_VERIFIED_HCA') || errorBody.includes('LOGIN_REQUEST_HCA_RESULT_WAS_NO_MEDICAL_PROFESSIONAL') || errorCode.includes('LOGIN_REQUEST_HCA_RESULT_WAS_NO_MEDICAL_PROFESSIONAL')) {
      this.openBlockedHCADialog();
      return;
    }

    if (errorBody.includes('non medical professional') || errorBody.includes('LOGIN_REQUEST_HCA_RESULT_WAS_NO_MEDICAL_PROFESSIONAL')) {
      this.emailApiError = this.translocoService.translate('errors.emailNotFound');
      return;
    }

    if (errorBody.includes('LOGIN_USER_OR_PASSWORD_INVALID') || errorBody.includes('El usuario o contraseña es incorrecto')) {
      this.emailApiError = this.translocoService.translate('errors.emailOrPasswordInvalid');
      return;
    }
    // Check for specific error types and map to form fields
    if (errorBody.code === 'AUTH_INVALID_CREDENTIALS' || errorBody.includes('No hay ning')) {
      // Invalid credentials - could be either email or password
      this.emailApiError = errorBody;
    } else if (errorBody.code === 'AUTH_USER_NOT_FOUND' || errorBody.includes('user not found')) {
      // User not found - email issue
      this.emailApiError = 'User not found';
    } else if (errorBody.code === 'AUTH_ACCOUNT_LOCKED' || errorBody.includes('locked')) {
      // Account locked
      this.emailApiError = 'Account is locked. Please contact support.';
    } else if (errorBody.fieldErrors) {
      // Field-specific errors from API
      errorBody.fieldErrors.forEach(fieldError => {
        if (fieldError.field === 'email') {
          this.emailApiError = fieldError.message;
        } else if (fieldError.field === 'password') {
          this.passwordApiError = fieldError.message;
        }
      });
    } else if (errorBody.includes('Recuerda que debes activar tu cuenta.')) {
      // Recuerda que debes activar tu cuenta.
    // TODO: add a specific error code for this case in backend, remove hardcoded string
      this.apiResendEmailError = errorBody;
      return;
    } else {
      // Generic error - show in both fields or use a general alert
      this.emailApiError = errorBody;
    }
  }
  
  openRegisterDialog(): void {
    // Use NavigationFlowService for proper transition handling
    this.navigationFlow.transitionLoginToRegister().subscribe(result => {
      console.log('[LoginDialog] Register dialog closed with result:', result);
      // If user completed registration or wants to return to login
      // the result will be handled automatically by NavigationFlowService
    });
  }

   openEmailVerifiedDialog(): void {
      if (this._dialogRef) {
        // Close without animation
        this._dialogRef.close();
      }
  
      const { width, height } = this.dialogUtilsService.calculateDialogSize();
  
      this._dialogRef = this._dialog.open(EmailVerifyStepComponent, {
        width: `${width}px`,
        //height: `${height}px`,
        maxWidth: '90vw',
        maxHeight: '65vh',
        autoFocus: false,
        disableClose: true,
        panelClass: 'custom-dialog-panel',
        // Disable enter animation
        enterAnimationDuration: '0ms',
        // Disable exit animation
        exitAnimationDuration: '0ms',
        data: {
          email: this.loginSessionForm.get('email').value
        }
      });
  
      // Set up responsive behavior and get cleanup function
      const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);
  
      this._dialogRef.afterClosed().subscribe(() => {
        // Clean up the event listener
        cleanupResizeListener();
        this._dialogRef = null;
      });
    }

  /**
   * Closes the dialog
   */
  closeDialog(): void {
    console.log('[LOGIN-DIALOG] closeDialog called, calling dialogUtilsService');
    this.dialogUtilsService.closeDialog(this._dialogRef);
    console.log('[LOGIN-DIALOG] dialogUtilsService.closeDialog completed');
  }

  handleEnterKey(event: KeyboardEvent): void {
    event.preventDefault();

    // Only proceed with login if form is valid
    if (this.loginSessionForm.valid) {
      this.login();
    }
  }

  openOtpDialog(): void {
    const email = this.loginSessionForm.get('email')?.value;

    if (this.loginSessionForm.get('email')?.invalid) {
      this.loginSessionForm.get('email')?.markAsTouched();
      return;
    }

    // validate that the email exists before opening OTP dialog
    this.ferrerAuth.verifiedEmail(email).subscribe({
      next: (exists) => {
        if (exists && exists.connect) {
          // Check if we're in iframe mode (check both runtime and session storage)
          const isInIframe = window.self !== window.top || sessionStorage.getItem('apiVersion') === '2.0';

          // Close current dialog
          const currentDialogRef = this._dialogRef;
          if (currentDialogRef) {
            // In iframe mode, close without action (to prevent duplicate OTP dialogs)
            // In standalone mode, close with action for app.component to handle
            if (isInIframe) {
              currentDialogRef.close();
            } else {
              currentDialogRef.close({ action: 'openOtp', email: email });
            }
          }

          // Wait for dialog to close cleanly before opening the next one
          setTimeout(() => {
            const { width, height } = this.dialogUtilsService.calculateDialogSize();

            if (isInIframe && typeof (window as any).openOtpDialog === 'function') {
              // Use the global method exposed by app.component
              (window as any).openOtpDialog(email);
            } else {
              // Direct dialog opening (not in iframe)
              const dialogRef = this._dialog.open(OtpDialogComponent, {
                width: `${width}px`,
                maxWidth: '90vw',
                autoFocus: false,
                disableClose: true,
                panelClass: 'custom-dialog-panel',
                data: { email: email }
              });

              const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(dialogRef);
              dialogRef.afterClosed().subscribe(() => {
                cleanupResizeListener();
              });
            }
          }, 50);
        } else {
          this.loginSessionForm.get('email')?.setErrors({ emailNotFound: true });
          this.loginSessionForm.get('email')?.markAsTouched();
        }
      },
      error: (error) => {
        console.error('Error checking email existence in OTP screen:', error);
      }
    });
  }

  opendiffCountryDialog(responses: any): void {
    // Close any existing dialog
    if (this._dialogRef) {
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(DiffCountryDialogComponent, {
      width: `${width}px`,
      maxWidth: '90vw',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.loginSessionForm.get('email')?.value,
        password: this.loginSessionForm.get('password').value,
        loginaction: "passwordLogin",
        consentsAceptados: responses.consents,
        userCountry: responses.country
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }

  openConsentDialog(response: any): void {
    // Close any existing dialog
    if (this._dialogRef) {
      this._dialogRef.close();
    }
    console.log(response);
    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(ConsentDialogComponent, {
      width: `${width}px`,
      maxWidth: '90vw',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.loginSessionForm.get('email')?.value,
        password: this.loginSessionForm.get('password').value,
        loginaction: "passwordLogin",
        consentsAceptados: response.consents,
        userCountry: response.country || response.userAccess?.country
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }
  openBlockedDialog(): void {
    // Close any existing dialog
    if (this._dialogRef) {
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(BlockedUserComponent, {
      width: `${width}px`,
      maxWidth: '90vw',
      maxHeight: '65vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.loginSessionForm.get('email')?.value,
        password: this.loginSessionForm.get('password').value,
        loginaction: "passwordLogin"
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }

  openBlockedHCADialog(): void {
    // Close any existing dialog
    if (this._dialogRef) {
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(BlockedMedicalComponent, {
      width: `${width}px`,
      maxWidth: '90vw',
      maxHeight: '65vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      data: {
        email: this.loginSessionForm.get('email')?.value,
        password: this.loginSessionForm.get('password').value,
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }
  static noWhitespaceValidator(control: FormControl): { [key: string]: boolean } | null {
    // Check if the input string has any spaces
    const containsSpaces = (control.value || '').includes(' ');
    return containsSpaces ? { 'containsSpaces': true } : null;
  }
  shouldShowCloseButton(): boolean {
    return true;
    return this.dialogUtilsService.shouldShowCloseButton();
  }
  private isRunningInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we get a security error, we're definitely in an iframe
      return true;
    }
  }
}
